import { formatDistanceToNow } from 'date-fns'
import { DAY_NAMES, type DayOfWeek } from '@backend/lib/db/schema/automation'
import type { AutomationPlatform } from '@backend/lib/db/schema/automation'
import { DAY_LABELS, DAY_SHORT, PLATFORM_LABELS, getToday } from './constants'

/**
 * A human day list, sorted in DAY_NAMES order by the caller:
 *   1 → "Monday"; 2 → "Monday and Wednesday";
 *   3–6 → "Monday, Wednesday and Friday"; 7 → "day" (reads "Every day, …").
 * `short` uses DAY_SHORT joined by " · " for compact node/badge use.
 */
export function formatDayList(days: DayOfWeek[], opts?: { short?: boolean }): string {
  const ordered = DAY_NAMES.filter((d) => days.includes(d))
  if (ordered.length === 7) return 'day'
  const labels = ordered.map((d) => (opts?.short ? DAY_SHORT[d] : DAY_LABELS[d]))
  if (opts?.short) return labels.join(' · ')
  if (labels.length <= 1) return labels[0] ?? ''
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/**
 * Persona: "Every Tuesday, Cassi drafts a LinkedIn + Instagram post as Maya and emails you."
 * Moodboard: "… post from Summer Launch and emails you." (as → from)
 * Multiple days read "Every Monday, Wednesday and Friday, …".
 */
/** "a" / "an" for the following word — vowel-initial platform labels take "an" (Instagram). */
export function indefiniteArticle(word: string): string {
  return /^[aeiou]/i.test(word) ? 'an' : 'a'
}

export function buildSentence(
  days: DayOfWeek[],
  source: { name: string },
  platforms: AutomationPlatform[],
): string {
  const platformList = platforms.map((p) => PLATFORM_LABELS[p]).join(' + ')
  return `Every ${formatDayList(days)}, Cassi drafts ${indefiniteArticle(platformList)} ${platformList} post from ${source.name} and emails you.`
}

/**
 * Today's day-of-week in the brand's timezone. `Intl` throws on an invalid
 * timezone string, so fall back to the browser's local day rather than crash.
 */
export function getTodayInTimezone(timezone: string | undefined): DayOfWeek {
  if (!timezone) return getToday()
  try {
    const weekday = new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      timeZone: timezone,
    })
      .format(new Date())
      .toLowerCase()
    return DAY_NAMES.find((d) => d === weekday) ?? getToday()
  } catch {
    return getToday()
  }
}

function brandHour(timezone: string | undefined): number {
  if (!timezone) return new Date().getHours()
  try {
    return Number(
      new Intl.DateTimeFormat('en-GB', {
        hour: 'numeric',
        hour12: false,
        timeZone: timezone,
      }).format(new Date()),
    )
  } catch {
    return new Date().getHours()
  }
}

/**
 * "Today, 8:00" / "Tomorrow, 8:00" / "Mon, 8:00" for the soonest upcoming day
 * among `days` — brand-tz aware. Today only counts before 08:00.
 */
export function nextRunLabel(days: DayOfWeek[], timezone: string | undefined): string {
  if (days.length === 0) return ''
  const today = getTodayInTimezone(timezone)
  const todayIndex = DAY_NAMES.indexOf(today)
  const beforeRun = brandHour(timezone) < 8

  if (beforeRun && days.includes(today)) return 'Today, 8:00'

  // Nearest day strictly ahead (wrapping the week). Today counts as "next week"
  // here since the 08:00 slot has already passed when we reach this point.
  let best: DayOfWeek | null = null
  let bestDelta = Infinity
  for (const day of days) {
    const delta = (DAY_NAMES.indexOf(day) - todayIndex + 7) % 7 || 7
    if (delta < bestDelta) {
      bestDelta = delta
      best = day
    }
  }
  if (!best) return ''
  if (bestDelta === 1) return 'Tomorrow, 8:00'
  return `${DAY_SHORT[best]}, 8:00`
}

/** "Generated 2 days ago" / "Not generated yet". */
export function lastRunLabel(lastTriggeredAt: string | Date | null | undefined): string {
  if (!lastTriggeredAt) return 'Not generated yet'
  return `Generated ${formatDistanceToNow(new Date(lastTriggeredAt), { addSuffix: true })}`
}
